<template>
  <actionable-panel
    header-text="How is your shipment configured?" :complete="complete" :active="active"
    :current-step="currentStep" :total-steps="totalSteps" :disable-edit="editDisabled" @edit="transitionEdit"
  >
    <div v-if="shouldIncludeDimensions" class="add-shipping-configuration__helper">
      We'll use these dimensions and weights for estimating costs and buying shipping labels.
    </div>
    <div v-if="hasWroParcelBoxLimit" class="add-shipping-configuration__helper">
      {{ wroParcelBox }} boxes allowed per parcel Receiving Order.
    </div>
    <expand-transition slot="table">
      <div>
        <template v-if="!floorLoadedContainerWithNoMultipleLots && isFloorLoadedContainer">
          <p class="wro-info-text">
            We require 1 WRO per container. When sending multiple containers, you must create 1 WRO
            for each container.
          </p>
        </template>
        <template v-if="floorLoadedContainerWithNoMultipleLots">
          <div class="wro-info-banner">
            <sb-banner variation="info">
              When sending inventory by Floor Loading Container, shipment configuration is not needed. Please select 'Save
              and Continue'.
            </sb-banner>
          </div>
        </template>
        <template v-else-if="isSingleSku">
          <div>
            <div class="add-shipping-configuration__banner">
              <h4 class="add-shipping-configuration__header">
                Single Sku Per {{ displayAsPallet ? 'Pallet' : 'Box' }}
              </h4>
            </div>
            <sb-table
              v-if="hasShippingConfigurations"
              :column-definitions="columnDefinitionsComputed"
              :rows="shippingConfigurations"
            >
              <sb-table-row slot="sb-table-row" slot-scope="{row}">
                <sb-table-cell>
                  <router-link :to="{ name: productsRoute, params: { productId: row.productId } }" target="_blank">
                    {{ row.productName }}
                  </router-link>
                  <p>{{ row.lotNumber }} {{ row.expirationDate }}</p>
                </sb-table-cell>
                <sb-table-cell>{{ row.shipmentQuantity }}</sb-table-cell>
                <sb-table-cell>
                  <div
                    v-for="(boxConfiguration, boxConfigId) in row.boxConfigurations"
                    :key="boxConfigId" class="add-shipping-configuration__row quantity-per-box-configuration"
                  >
                    <sb-field :variation="errorVariation(row)">
                      <sb-text-input
                        :value="boxConfiguration.quantityPerBox | displayValue"
                        @input="(newQuantity) => handleChangeQuantityPerBox(row, boxConfigId, newQuantity, row.shipmentQuantity)"
                      />
                    </sb-field>
                  </div>
                </sb-table-cell>
                <sb-table-cell>
                  <div
                    v-for="(boxConfiguration, boxConfigId) in row.boxConfigurations"
                    :key="boxConfigId" class="add-shipping-configuration__row number-of-box-configuration"
                  >
                    <sb-field :variation="errorVariation(row)">
                      <sb-text-input
                        :value="boxConfiguration.numberOfBoxes | displayValue"
                        @input="(newBoxes) => handleChangeNumberOfBoxes(row, boxConfigId, newBoxes, row.shipmentQuantity)"
                      />
                    </sb-field>
                  </div>
                </sb-table-cell>
                <sb-table-cell>
                  <span :style="totalQuantityErrorStyle(row)">
                    {{ computedTotalQuantity(row) }}
                  </span>
                </sb-table-cell>
                <sb-table-cell v-if="shouldIncludeDimensions">
                  <div
                    v-for="(boxConfiguration, boxConfigId) in row.boxConfigurations"
                    :key="boxConfigId" class="add-shipping-configuration__row add-shipping-configuration__box-dimensions-configuration"
                  >
                    <sb-field
                      class="add-shipping-configuration__quantity-dimensions"
                      :variation="errorDimensionsVariation(boxConfiguration)"
                    >
                      <sb-text-input
                        :value="boxConfiguration.length | displayValue"
                        append="L"
                        @input="(val) => handleChangeBoxDimensions(row.id, boxConfigId, 'length', val, boxConfiguration)"
                      />
                    </sb-field> x
                    <sb-field
                      class="add-shipping-configuration__quantity-dimensions"
                      :variation="errorDimensionsVariation(boxConfiguration)"
                    >
                      <sb-text-input
                        :value="boxConfiguration.width | displayValue"
                        append="W"
                        @input="(val) => handleChangeBoxDimensions(row.id, boxConfigId, 'width', val, boxConfiguration)"
                      />
                    </sb-field> x
                    <sb-field
                      class="add-shipping-configuration__quantity-dimensions"
                      :variation="errorDimensionsVariation(boxConfiguration)"
                    >
                      <sb-text-input
                        :value="boxConfiguration.height | displayValue"
                        append="H"
                        @input="(val) => handleChangeBoxDimensions(row.id, boxConfigId, 'height', val, boxConfiguration)"
                      />
                    </sb-field>
                  </div>
                </sb-table-cell>
                <sb-table-cell v-if="shouldIncludeDimensions">
                  <div
                    v-for="(boxConfiguration, boxConfigId) in row.boxConfigurations"
                    :key="boxConfigId" class="add-shipping-configuration__row add-shipping-configuration__box-dimensions-configuration"
                  >
                    <sb-field
                      class="add-shipping-configuration__quantity-weight"
                      :variation="errorWeightVariation(boxConfiguration)"
                    >
                      <sb-text-input
                        :value="(boxConfiguration.weight) ? boxConfiguration.weight.pounds : 0 | displayValue"
                        append="lb"
                        @input="(val) => handleChangeBoxWeight(row.id, boxConfigId, 'pounds', val, boxConfiguration)"
                      />
                    </sb-field> x
                    <sb-field
                      class="add-shipping-configuration__quantity-weight"
                      :variation="errorWeightVariation(boxConfiguration)"
                    >
                      <sb-text-input
                        :value="(boxConfiguration.weight) ? boxConfiguration.weight.ounces : 0 | displayValue"
                        append="oz"
                        @input="(val) => handleChangeBoxWeight(row.id, boxConfigId, 'ounces', val, boxConfiguration)"
                      />
                    </sb-field>
                  </div>
                </sb-table-cell>
                <sb-table-cell>
                  <div
                    v-for="(boxConfiguration, boxConfigId) in row.boxConfigurations"
                    :key="boxConfigId" class="add-shipping-configuration__row add-shipping-configuration__action"
                  >
                    <span
                      v-if="boxConfigId !== 0"
                      class="icon-trash add-shipping-configuration__action-button add-shipping-configuration__action-button--remove"
                      href="#" @click.prevent="handleRemoveQuantity(row.id, boxConfigId, row.shipmentQuantity)"
                    />
                    <a
                      v-else href="#"
                      class="add-shipping-configuration__action-button add-shipping-configuration__action-button-icon icon icon-plus-circle"
                      @click.prevent="handleAddBoxConfiguration(row.id)"
                    >
                      <sb-tooltip
                        class="add-shipping-configuration__action-button--hover"
                        orientation="top" @click.native.prevent.stop="noop"
                      >
                        <span>Add Configuration</span>
                      </sb-tooltip>
                    </a>
                  </div>
                </sb-table-cell>
              </sb-table-row>
            </sb-table>
          </div>
        </template>
        <template v-else>
          <MultiSkuConfigurationTable :machine="machine" :should-include-dimensions="shouldIncludeDimensions" />
        </template>
      </div>
    </expand-transition>
    <div slot="footer" class="wro-actionable-panel__footer wro-actionable-panel__footer--flex-between">
      <div>
        <sb-async-button
          :loading-promise="savingWro" :disabled="disableSave"
          class="wro-save-button wro-actionable-panel__action" @click="transitionSave"
        >
          Save & Continue
        </sb-async-button>
        <a class="wro-actionable-panel__action" href="#" @click.prevent="transitionClear">Cancel</a>
      </div>
      <div class="wro-actionable-panel__text wro-actionable-panel__text--light wro-actionable-panel__text--bold">
        ({{ computeBoxNumberString }})
      </div>
    </div>
  </actionable-panel>
</template>
<script>
import MultiSkuConfigurationTable from './MultiSkuConfigurationTable.vue'
// import differenceWith from 'lodash/differenceWith'
import _sumBy from 'lodash/sumBy'
import _flatMap from 'lodash/flatMap'
import stepsMixin from '../stepsMixin'
import { ProductsEnum } from '@/enums/modules/routes'
import tokens from 'shipbob-design-system/dist/system/tokens/tokens.json'
import { noop } from '@/utils/common'
import {
  computedTotalQuantity,
  validateBoxDimensions,
  validateBoxWeight,
  validateTotalQuantity,
  isValidSingleSkuHelper
} from './helper'
import SystemSettingMixin from '@/mixins/systemSettings'

const ERROR_CLASS = 'error'
const DEFAULT_CLASS = 'default'

export default {
  name: 'AddShippingConfigurations',
  components: {
    MultiSkuConfigurationTable
  },
  directives: {},
  filters: {
    displayValue (value) {
      return `${value}`
    }
  },
  mixins: [stepsMixin, SystemSettingMixin],
  props: {},
  data: () => ({
    loading: false,
    productsRoute: ProductsEnum.ProductDetails,
    invalidQuantities: [],
    noop,
    wroParcelBox: null
  }),
  computed: {
    computeBoxNumberString () {
      let boxNumberStringConfig = 'shipmentConfiguration.box'
      if (this.isFloorLoadedContainer) {
        boxNumberStringConfig = 'shipmentConfiguration.container'
      } else if (this.displayAsPallet) {
        boxNumberStringConfig = 'shipmentConfiguration.pallet'
      }
      return this.$i18n.tc(boxNumberStringConfig, this.totalNumberOfBoxes, { n: this.totalNumberOfBoxes })
    },
    totalNumberOfBoxesPerSingleSku () {
      return _sumBy(_flatMap(this.shippingConfigurations.map(sc => sc.boxConfigurations.map(bc => bc))), o => o.numberOfBoxes)
    },
    totalNumberOfBoxesPerMultiSku () {
      return _sumBy(this.multiSkuShippingConfigurations, o => o.skuConfigurations.length > 0 ? o.skuConfigurations[0].numberOfBoxes : 0)
    },
    totalNumberOfBoxes () {
      return this.isSingleSku ? this.totalNumberOfBoxesPerSingleSku : this.totalNumberOfBoxesPerMultiSku
    },
    columnDefinitionsComputed () {
      return [
        { name: 'Item Information', field: 'productInfo' },
        { name: 'Shipment Quantity', field: 'shipmentQuantity' },
        { name: (this.displayAsPallet ? 'SKU Quantity Per Pallet' : 'Quantity per box'), field: 'quantityPerBox' },
        { name: (this.displayAsPallet ? '# of Pallets' : '# of boxes'), field: 'numberOfBoxes' },
        { name: 'Total Quantity', field: 'totalQuantity' },
        ...(this.shouldIncludeDimensions ? [{ name: 'Box Dimensions (in inches)', field: 'boxDimensions' }, { name: 'Weight', field: 'boxWeight' }] : []),
        { name: 'Action', field: 'action' }
      ]
    },
    displayAsPallet () {
      return this.draft.packagingType && this.draft.packagingType.id === 2
    },
    hasShippingConfigurations () {
      return this.shippingConfigurations.length > 0
    },
    shippingConfigurations () {
      return this.$shipBobStore.getters[this.StoreModuleName + '/shippingConfigurations'] || []
    },
    multiSkuShippingConfigurations () {
      return this.$shipBobStore.getters[this.StoreModuleName + '/multiSkuShippingConfigurations'] || []
    },
    isSingleSku () {
      return this.currentStateName.indexOf('singleSku') > -1
    },
    shouldIncludeDimensions () {
      const isShipBobLabels = this.$shipBobStore.getters[this.StoreModuleName + '/isShipbobLabels']
      return this.wroLabelPurchaseFlag && isShipBobLabels
    },
    floorLoadedContainerWithNoMultipleLots () {
      return this.$shipBobStore.getters[this.StoreModuleName + '/floorLoadedContainerWithNoMultipleLots']
    },
    selectedPackagingType () {
      return this.$shipBobStore.getters[this.StoreModuleName + '/selectedPackagingType']
    },
    isFloorLoadedContainer () {
      return this.selectedPackagingType && this.selectedPackagingType.id === 3
    }
  },
  watch: {
    shippingConfigurations: {
      handler: function () {
        this.validateConfigurations()
      },
      deep: true
    }
  },
  destroyed () { },
  methods: {
    // user actions
    handleAddBoxConfiguration (id) {
      const existingShippingConfiguration = this.shippingConfigurations.find(p => p.id === id)
      if (existingShippingConfiguration && existingShippingConfiguration.boxConfigurations.length >= 20) return
      this.$shipBobStore.dispatch(this.StoreModuleName + '/addBoxConfiguration', id)
    },
    handleRemoveQuantity (id, boxConfigId, shipmentQuantity) {
      this.$shipBobStore.dispatch(this.StoreModuleName + '/removeBoxConfiguration', { id, boxConfigId })
    },
    handleChangeQuantityPerBox (boxConfig, boxConfigId, quantity, shipmentQuantity) {
      const id = this.findBoxConfigIndex(boxConfig)
      const parsedValue = this.sanitizeNumber(quantity)
      this.$shipBobStore.dispatch(this.StoreModuleName + '/updateQuantityPerBox', { id, boxConfigId, quantityPerBox: parsedValue })
    },
    findBoxConfigIndex (boxConfig) {
      return this.shippingConfigurations.findIndex(x => x.productId === boxConfig.productId && x.lotNumber === boxConfig.lotNumber && x.expirationDate === boxConfig.expirationDate)
    },
    handleChangeNumberOfBoxes (boxConfig, boxConfigId, numberOfBoxes, shipmentQuantity) {
      const id = this.findBoxConfigIndex(boxConfig)
      const parsedValue = this.sanitizeNumber(numberOfBoxes)
      this.$shipBobStore.dispatch(this.StoreModuleName + '/updateNumberOfBoxes', { id, boxConfigId, numberOfBoxes: parsedValue })
    },
    handleChangeBoxDimensions (id, boxConfigId, prop, value, boxConfiguration) {
      const parsedValue = this.sanitizeNumber(value)
      boxConfiguration[prop] = parsedValue
      this.$shipBobStore.dispatch(this.StoreModuleName + '/updateBoxConfigurations', { id, boxConfigId, boxConfiguration })
    },
    handleChangeBoxWeight (id, boxConfigId, prop, value, boxConfiguration) {
      const parsedValue = this.sanitizeNumber(value)
      boxConfiguration.weight[prop] = parsedValue
      this.$shipBobStore.dispatch(this.StoreModuleName + '/updateBoxConfigurations', { id, boxConfigId, boxConfiguration })
    },
    computedTotalQuantity (boxConfig) {
      return computedTotalQuantity(boxConfig)
    },
    // validations
    sanitizeNumber (value) {
      const parsedNumber = parseInt(value)
      return isNaN(parsedNumber) ? 0 : parsedNumber
    },
    validateConfigurations () {
      const valid = isValidSingleSkuHelper(this.shippingConfigurations, this.shouldIncludeDimensions)

      if (valid) {
        this.machine.transition('ALL_QUANTITIES_ADDED')
      } else {
        this.machine.transition('INVALID_CONFIGURATION')
      }
    },
    // computed style
    totalQuantityErrorStyle (shippingConfig) {
      return this.errorVariation(shippingConfig) === ERROR_CLASS ? { 'border-color': tokens.color_error, color: tokens.color_error } : ''
    },
    errorVariation (shippingConfig) {
      return (validateTotalQuantity(shippingConfig, shippingConfig.shipmentQuantity)) ? DEFAULT_CLASS : ERROR_CLASS
    },
    errorDimensionsVariation (boxConfig) {
      return (validateBoxDimensions(boxConfig)) ? DEFAULT_CLASS : ERROR_CLASS
    },
    errorWeightVariation (boxConfig) {
      return (validateBoxWeight(boxConfig)) ? DEFAULT_CLASS : ERROR_CLASS
    }
  },
  i18n: {
    messages: {
      'en-US': {
        shipmentConfiguration: {
          box: '{n} Boxes | {n} Box | {n} Boxes',
          pallet: '{n} Pallets | {n} Pallet | {n} Pallets',
          container: '{n} Containers | {n} Container | {n} Containers'
        }
      }
    }
  }
}
</script>

<style lang='scss'>
@import '../panel.scss';
@import 'node_modules/shipbob-design-system/dist/system/tokens/tokens.scss';

.add-shipping-configuration {
  &__banner {
    background-color: $color-lightgray;
    padding: 0.5rem 1.75rem;
  }

  &__header {
    text-transform: uppercase;
    font-size: 0.75rem;
    margin-bottom: 0;
  }

  &__helper {
    color: $color-grey;
    font-size: 1rem;
    font-weight: 400;
  }

  &__row {
    margin-bottom: 1em;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  &__action {
    margin-bottom: 2.3em;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__action-button {
    text-decoration: none;
    color: #2671af;

    &--bold {
      font-weight: 600;
    }

    &--remove {
      @extend .icon-trash !optional;
      cursor: pointer;
      color: $color-grey;
    }

    &--hover {
      cursor: auto;
      font-size: 0.8rem;
      padding: 0.5rem;
    }
  }

  &__action-button-icon {
    font-size: 1.25em;
  }

  &__box-dimensions-configuration {
    display: flex;
    align-items: center;
  }

  &__quantity-dimensions {
    width: 4.5rem;
    padding-right: 5px;
    padding-left: 5px;
  }

  &__quantity-weight {
    width: 4.5rem;
    padding-right: 5px;
    padding-left: 5px;
  }
}

.wro-info-banner {
  margin: 0 2em 3em 2em;
}

.wro-info-text {
  margin-left: 2em;
}
</style>
